import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import type { LiveMembership } from '../types/liveCall';

// ── Normalise ─────────────────────────────────────────────────────────────────

function normalizeMembership(data: Record<string, unknown>, id: string): LiveMembership {
  const toISO = (v: unknown): string => {
    if (v instanceof Timestamp) return v.toDate().toISOString();
    if (typeof v === 'string') return v;
    return new Date().toISOString();
  };
  return {
    ...(data as unknown as LiveMembership),
    id,
    cycleStartAt: toISO(data.cycleStartAt),
    cycleEndAt: toISO(data.cycleEndAt),
    createdAt: toISO(data.createdAt),
    updatedAt: toISO(data.updatedAt),
  };
}

// ── Read ──────────────────────────────────────────────────────────────────────

/** Returns the first active membership for this player+coach pair, or null. */
export async function getActiveMembershipForPlayerAndCoach(
  playerId: string,
  coachId: string,
): Promise<LiveMembership | null> {
  const q = query(
    collection(db, 'liveMemberships'),
    where('playerId', '==', playerId),
    where('coachId', '==', coachId),
    where('status', 'in', ['active', 'cancelling']),
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return normalizeMembership(snap.docs[0].data(), snap.docs[0].id);
}

/** Live subscription to a player's memberships. */
export function subscribeToPlayerMemberships(
  playerId: string,
  onChange: (memberships: LiveMembership[]) => void,
  /** Without it an error reads as no memberships, as before. */
  onError?: (err: unknown) => void,
): () => void {
  const q = query(
    collection(db, 'liveMemberships'),
    where('playerId', '==', playerId),
    where('status', 'in', ['active', 'cancelling', 'pending_payment']),
  );
  return onSnapshot(q, (snap) => {
    onChange(snap.docs.map((d) => normalizeMembership(d.data(), d.id)));
  }, (err) => (onError ? onError(err) : onChange([])));
}

/** A coach's members: Monthly Coaching that is active or ending. */
export function subscribeToCoachMemberships(
  coachId: string,
  onChange: (memberships: LiveMembership[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveMemberships'),
    where('coachId', '==', coachId),
    where('status', 'in', ['active', 'cancelling']),
  );
  return onSnapshot(q, (snap) => {
    onChange(snap.docs.map((d) => normalizeMembership(d.data(), d.id)));
  }, () => onChange([]));
}

/** Live subscription to a single membership doc. */
export function subscribeMembership(
  membershipId: string,
  onChange: (m: LiveMembership | null) => void,
): () => void {
  return onSnapshot(doc(db, 'liveMemberships', membershipId), (snap) => {
    onChange(snap.exists() ? normalizeMembership(snap.data(), snap.id) : null);
  }, () => onChange(null));
}

// ── Cloud Function callers ────────────────────────────────────────────────────

export interface CreateMembershipParams {
  offeringId: string;
  paymentMethodId?: string;
  sessionId?: string;
}

export interface CreateMembershipResult {
  membershipId: string;
  /** Null until period 1 starts (first completed lesson or day 14). */
  subscriptionId: string | null;
  clientSecret: string | null;
  alreadyActive: boolean;
}

const _createLiveMembership = httpsCallable<CreateMembershipParams, CreateMembershipResult>(
  functions,
  'createLiveMembership',
);
export async function createLiveMembershipFn(
  params: CreateMembershipParams,
): Promise<CreateMembershipResult> {
  const { data } = await _createLiveMembership(params);
  return data;
}

const _confirmLiveMembership = httpsCallable<
  { membershipId: string },
  { status: string; cycleStartAt?: string; cycleEndAt?: string }
>(functions, 'confirmLiveMembership');
export async function confirmLiveMembershipFn(membershipId: string): Promise<void> {
  await _confirmLiveMembership({ membershipId });
}

const _cancelLiveMembership = httpsCallable<
  { membershipId: string },
  { status: string }
>(functions, 'cancelLiveMembership');
export async function cancelLiveMembershipFn(membershipId: string): Promise<void> {
  await _cancelLiveMembership({ membershipId });
}
